/* District Bangle — gift-finder.js

   The recipient picks a PATH, and the path decides both the questions asked and
   the rule that builds the result. One question set for everybody was the old
   shape, and it could not serve three different jobs:

     gift        metal -> her month -> your month   her stone + your stone (+ neutral)
     self        metal -> month                     her stone + her tennis (+ neutral)
   The neutral tops a short stack up to three; it is not added when three
   matched bangles already resolved.
     bridesmaids metal -> dress colour -> how many  ONE tennis bangle, N of it

   The bridesmaid path is why quantity exists at all: buying five of the same
   bracelet is not five different stacks, and the old adder could only ever send
   three distinct items with quantity 1.

   Metal answers pick a VARIANT. Every bangle here carries a "Metal Finish"
   option, so gold and silver are one product; matching on the variant title is
   what keeps "in her finish" true all the way into the cart.

   Rules, not AI. Products are added at full price; the discount is the
   merchant's automatic cart rule. */
import { BaseElement, define, money, bus } from '@theme/global';

const PATHS = {
  gift: ['metal', 'month', 'month2'],
  self: ['metal', 'month'],
  bridesmaids: ['metal', 'dress', 'qty'],
};

class GiftFinder extends BaseElement {
  init() {
    this.pct = Number(this.dataset.pct) || 20;
    this.unit = Number(this.dataset.unit) || 2500;
    this.data = this.json('[data-gf-data]', {});
    this.stones = this.data.stones || [];
    this.dresses = this.data.dresses || [];
    this.metals = this.data.metals || [];
    this.copy = this.data.copy || {};
    this.byKey = {};
    this.stones.forEach((s) => { this.byKey[s.key] = s; });

    this.panels = [...this.querySelectorAll('[data-q]')];
    this.prog = this.querySelector('[data-prog]');
    this.briefList = this.querySelector('[data-brief-list]');
    this.reset();

    this.querySelectorAll('[data-recipient]').forEach((b) => this.listen(b, 'click', () => {
      this.state.path = PATHS[b.dataset.path] ? b.dataset.path : 'gift';
      this.state.title = b.dataset.title;
      this.state.recipient = b.dataset.label;
      this.seq = ['recipient', ...PATHS[this.state.path]];
      this.renderBrief();
      this.go(1);
    }));

    this.querySelectorAll('[data-metal]').forEach((b) => this.listen(b, 'click', () => {
      // The neutral bangle belongs to the finish that selects it: Shopify allows
      // one product picker in a section's settings, and a per-finish neutral is
      // the honest shape anyway — gold and silver are different products.
      const meta = this.metals[Number(b.dataset.idx)] || {};
      this.state.metal = {
        label: b.dataset.label,
        match: (b.dataset.match || b.dataset.label || '').toLowerCase(),
        neutral: meta.neutral || null,
      };
      this.advance();
    }));

    this.querySelectorAll('[data-month]').forEach((b) => this.listen(b, 'click', () => {
      // The month grid is rendered once and visited twice on the gift path —
      // hers, then yours. Which slot it fills is the step, not the button.
      this.state[this.current() === 'month2' ? 'month2' : 'month'] = b.dataset.key;
      this.advance();
    }));

    this.querySelectorAll('[data-dress]').forEach((b) => this.listen(b, 'click', () => {
      this.state.dress = b.dataset.key;
      this.advance();
    }));

    // One attribute, carrying the number. It was written as `data-qty data-qty="5"`
    // — the same attribute twice, so the browser kept the first (empty) one and
    // every shortcut resolved to the fallback 3.
    this.querySelectorAll('[data-qty-pick]').forEach((b) => this.listen(b, 'click', () => {
      const n = Number(b.dataset.qtyPick);
      if (!Number.isFinite(n) || n < 1) return;
      this.state.qty = n;
      const input = this.querySelector('[data-qty-input]');
      if (input) input.value = String(n);   // the two controls answer one question
      this.advance();
    }));
    const qtyIn = this.querySelector('[data-qty-input]');
    if (qtyIn) {
      this.listen(qtyIn, 'change', () => { this.state.qty = Math.max(1, Number(qtyIn.value) || 3); });
      const qtyGo = this.querySelector('[data-qty-go]');
      if (qtyGo) this.listen(qtyGo, 'click', () => { this.state.qty = Math.max(1, Number(qtyIn.value) || 3); this.advance(); });
    }

    const skip = this.querySelector('[data-skip]');
    if (skip) this.listen(skip, 'click', () => {
      this.state[this.current() === 'month2' ? 'month2' : 'month'] = null;
      this.state.skipped = true;
      this.advance();
    });

    this.querySelectorAll('[data-back]').forEach((b) => this.listen(b, 'click', () => this.go(Math.max(0, this.state.step - 1))));

    // Kept guarded rather than deleted: the result panel now offers Back
    // instead of Start over, but the reset is still correct if a Start over
    // control is ever put back.
    const restart = this.querySelector('[data-restart]');
    if (restart) this.listen(restart, 'click', () => {
      this.reset();
      const add = this.querySelector('[data-add]');
      if (add) { add.classList.remove('is-added'); add.textContent = this.addLabel; }
      const err = this.querySelector('[data-error]'); if (err) err.hidden = true;
      this.renderBrief();
      this.go(0);
    });

    const add = this.querySelector('[data-add]');
    if (add) { this.addLabel = add.textContent; this.listen(add, 'click', () => this.addStack()); }

    this.renderBrief();
    this.go(0);
  }

  json(sel, fallback) {
    const el = this.querySelector(sel);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (_) { return fallback; }
  }

  reset() {
    this.state = { step: 0, path: 'gift', title: '', recipient: '', metal: null,
                   month: null, month2: null, dress: null, qty: 3, skipped: false };
    this.seq = ['recipient', ...PATHS.gift];
    this.lines = [];
  }

  current() { return this.seq[this.state.step]; }

  advance() {
    const next = this.state.step + 1;
    this.renderBrief();
    if (next >= this.seq.length) this.result(); else this.go(next);
  }

  /* ---------- steps ---------- */

  go(step) {
    this.state.step = step;
    const name = step >= this.seq.length ? 'result' : this.seq[step];
    this.panels.forEach((el) => { el.hidden = el.dataset.q !== name; });

    const t = this.querySelector(`[data-q="${name}"] [data-q-title]`);
    if (t && this.copy[name]) t.textContent = this.copy[name];

    const n = this.querySelector(`[data-q="${name}"] [data-q-num]`);
    if (n) n.textContent = (this.copy.q_of || 'Question {n} of {total}')
      .replace('{n}', String(step)).replace('{total}', String(this.seq.length - 1));

    if (this.prog) {
      this.prog.textContent = '';
      for (let i = 1; i < this.seq.length; i += 1) {
        const seg = document.createElement('span');
        seg.className = 'dbgf__prog-seg' + (i <= step ? ' is-on' : '');
        this.prog.appendChild(seg);
      }
    }
    this.renderBrief();
  }

  /* ---------- the brief ---------- */

  briefRows() {
    const rows = [];
    const L = this.copy.labels || {};
    rows.push({ k: L.recipient || 'For', v: this.state.recipient, step: 0 });
    this.seq.slice(1).forEach((name, i) => {
      const step = i + 1;
      if (name === 'metal') rows.push({ k: L.metal || 'Finish', v: this.state.metal && this.state.metal.label, step });
      if (name === 'month') rows.push({ k: this.state.path === 'gift' ? (L.her_month || 'Her month') : (L.month || 'Birth month'), v: this.monthName(this.state.month), step });
      if (name === 'month2') rows.push({ k: L.your_month || 'Your month', v: this.monthName(this.state.month2), step });
      if (name === 'dress') rows.push({ k: L.dress || 'Dress colour', v: this.dressName(this.state.dress), step });
      if (name === 'qty') rows.push({ k: L.qty || 'How many', v: this.state.qty ? String(this.state.qty) : '', step });
    });
    return rows;
  }

  monthName(key) { const s = this.byKey[key]; return s ? `${s.month} · ${s.stone}` : (key === null && this.state.skipped ? (this.copy.surprise || 'Surprise me') : ''); }
  dressName(key) { const d = this.dresses.find((x) => x.key === key); return d ? d.label : ''; }

  renderBrief() {
    if (!this.briefList) return;
    const empty = this.copy.empty || 'Not chosen yet';
    this.briefList.textContent = '';
    this.briefRows().forEach((row) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dbgf__brief-row';
      btn.disabled = !row.v;
      if (row.v) btn.addEventListener('click', () => this.go(row.step));
      const k = document.createElement('span'); k.className = 'dbgf__brief-k'; k.textContent = row.k;
      const v = document.createElement('span'); v.className = 'dbgf__brief-v'; v.textContent = row.v || empty;
      const e = document.createElement('span'); e.className = 'dbgf__brief-edit'; e.setAttribute('aria-hidden', 'true');
      e.textContent = this.copy.edit || 'Change';
      btn.append(k, v, e);
      li.appendChild(btn);
      this.briefList.appendChild(li);
    });
  }

  /* ---------- the rules ---------- */

  variantFor(p) {
    if (!p || !p.variants || !p.variants.length) return null;
    // Both sides lowercased. The needle comes from the merchant's "Matches the
    // variant containing" field, which is sentence case ("Gold"), and it was
    // being searched for inside an ALREADY lowercased variant title — so
    // "gold plated".indexOf("Gold") was -1 on every product and the finish the
    // shopper picked silently fell through to "first available variant".
    const want = ((this.state.metal && this.state.metal.match) || '').toLowerCase().trim();
    const hit = want && p.variants.find((v) => v.a && v.t.toLowerCase().indexOf(want) >= 0);
    return hit || p.variants.find((v) => v.a) || p.variants[0];
  }

  line(p, qty) {
    const v = this.variantFor(p);
    if (!p || !v) return null;
    return { title: p.title, url: p.url, img: (v && v.img) || p.img, vid: v.id, price: v.p, qty: qty || 1 };
  }

  build() {
    if (this.state.path === 'bridesmaids') {
      const d = this.dresses.find((x) => x.key === this.state.dress);
      return [this.line(d && d.product, Math.max(1, this.state.qty || 3))].filter(Boolean);
    }

    // The bangles chosen BY the answers come first and alone. Whether the
    // neutral is needed can only be decided once we know how many of them
    // actually resolved — a month with no product wired, or a stone with no
    // Color Tennis counterpart (June has none), quietly drops one.
    const her = this.byKey[this.state.month] || this.byKey[(this.stones[0] || {}).key];
    const matched = [this.line(her && her.product, 1)];

    if (this.state.path === 'gift') {
      const yours = this.byKey[this.state.month2];
      matched.push(this.line(yours && yours.product, 1));
    } else {
      // "a Color Tennis bangle" — the one wired to her stone, so the bangle
      // still belongs to her rather than being an arbitrary pick.
      matched.push(this.line(her && her.tennis, 1));
    }

    const out = matched.filter(Boolean);

    // Top the stack up to three with the plain District Flex Bangle in the
    // chosen finish. Three is not decorative: the store's offer is 20% off any
    // three, so a two-bangle result is a recommendation that costs the shopper
    // the discount. Appended rather than led with, so the stack reads as "the
    // ones we matched, plus one to complete it".
    if (out.length < 3) {
      // Quantity, not extra lines: June has no Color Tennis counterpart, so
      // that path resolves ONE matched bangle and a single neutral would leave
      // the stack at two — still short of the discount. Two plain bangles
      // flanking her stone is also how these are actually worn.
      const neutral = this.line(this.state.metal && this.state.metal.neutral, 3 - out.length);
      // Never the same bangle on two lines: a stone wired to the neutral
      // product would otherwise show as two separate lines of one.
      const dup = neutral && out.find((l) => l.vid === neutral.vid);
      if (dup) dup.qty = 3 - out.length + dup.qty;
      else if (neutral) out.push(neutral);
    }
    return out;
  }

  result() {
    this.lines = this.build();
    this.state.step = this.seq.length;
    this.panels.forEach((el) => { el.hidden = el.dataset.q !== 'result'; });

    const title = this.querySelector('[data-res-title]');
    if (title) title.textContent = this.state.title || this.copy.result_title || 'Your stack';

    const why = this.querySelector('[data-res-why]');
    if (why) why.textContent = this.whyLine();

    const holder = this.querySelector('[data-res-stones]');
    if (holder) {
      holder.textContent = '';
      this.lines.forEach((l) => {
        const cell = document.createElement(l.url ? 'a' : 'div');
        cell.className = 'dbgf__rstone';
        if (l.url) cell.href = l.url;
        const box = document.createElement('div'); box.className = 'dbgf__rstone-img';
        if (l.img) { const im = document.createElement('img'); im.src = l.img; im.alt = l.title; im.loading = 'lazy'; box.appendChild(im); }
        const nm = document.createElement('div'); nm.className = 'dbgf__rstone-name'; nm.textContent = l.title;
        const sub = document.createElement('div'); sub.className = 'dbgf__rstone-month';
        sub.textContent = l.qty > 1 ? `× ${l.qty}` : (this.state.metal ? this.state.metal.label : '');
        cell.append(box, nm, sub);
        holder.appendChild(cell);
      });
    }

    const gross = this.lines.reduce((sum, l) => sum + (l.price || this.unit) * l.qty, 0);
    const units = this.lines.reduce((sum, l) => sum + l.qty, 0);
    // The discount is the merchant's cart rule and it needs the minimum met —
    // promising 20% off two bangles would be a number the cart then refuses.
    const earns = units >= 3;
    const save = earns ? Math.round(gross * (this.pct / 100)) : 0;
    this.setText('[data-res-total]', money(gross - save));
    this.setText('[data-res-was]', save ? money(gross) : '');
    const saveEl = this.querySelector('[data-res-save]');
    if (saveEl) saveEl.hidden = !earns;

    if (this.prog) this.prog.querySelectorAll('.dbgf__prog-seg').forEach((s) => s.classList.add('is-on'));
    this.renderBrief();
  }

  whyLine() {
    const finish = this.state.metal ? this.state.metal.label.toLowerCase() : '';
    if (this.state.path === 'bridesmaids') {
      const d = this.dresses.find((x) => x.key === this.state.dress);
      return (this.copy.why_bridesmaids || 'The same bangle for every bridesmaid — {colour}, {finish}, {n} of them.')
        .replace('{colour}', d ? d.label : '')
        .replace('{finish}', finish)
        .replace('{n}', String(this.state.qty || 3));
    }
    const her = this.byKey[this.state.month];
    const stone = her ? her.stone : '';
    return (this.copy.why || 'Three clasp-free bangles in {finish}, built around {stone}. One size fits, gift-ready in a box.')
      .replace('{finish}', finish)
      .replace('{stone}', stone || (this.copy.surprise_stone || 'a wear-anywhere mix'));
  }

  setText(sel, text) { const el = this.querySelector(sel); if (el) el.textContent = text; }

  async addStack() {
    const items = this.lines.filter((l) => l.vid).map((l) => ({ id: l.vid, quantity: l.qty }));
    const btn = this.querySelector('[data-add]');
    const err = this.querySelector('[data-error]');
    const missing = this.lines.length === 0 || items.length !== this.lines.length;
    if (missing) {
      if (err) {
        err.textContent = this.copy.unlinked || 'Some pieces in this stack are not linked to a product yet.';
        err.hidden = false;
      }
      return;
    }
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(window.theme.routes.cart_add, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      bus.emit('cart:updated', { open: true });
      btn.classList.add('is-added');
      btn.textContent = window.theme.strings.added || 'Added';
      if (err) err.hidden = true;
    } catch (e) {
      if (err) { err.textContent = e.message || 'Could not add the stack.'; err.hidden = false; }
    } finally { btn.removeAttribute('aria-busy'); }
  }
}

define('gift-finder', GiftFinder);
